import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
// utils
import { fToNow } from 'src/utils/format-time';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import FileThumbnail from 'src/components/file-thumbnail';

// ----------------------------------------------------------------------

const getOverdueColor = (daysLate) => {
  if (daysLate >= 7) return 'error';
  if (daysLate >= 3) return 'warning';
  return 'info';
};

const getOverdueText = (daysLate) => {
  if (daysLate === 0) return 'Due Today';
  return `${daysLate} Day${daysLate > 1 ? 's' : ''} Late`;
};

const getDaysRemainingColor = (days) => {
  if (days < 0) return 'error';
  if (days <= 3) return 'warning';
  return 'info';
};

const getDaysRemainingText = (days) => {
  if (days < 0) return `${Math.abs(days)} Days Overdue`;
  if (days === 0) return 'Due Today';
  return `${days} Days Remaining`;
};

export default function NotificationItem({ notification, onClick }) {
  const [expanded, setExpanded] = useState(false);

  // Safely parse additional_data
  let extraData = null;
  if (notification.additional_data) {
    try {
      extraData = typeof notification.additional_data === 'string'
        ? JSON.parse(notification.additional_data)
        : notification.additional_data;
    } catch (e) {
      extraData = null;
    }
  }

  const hasExtraDetails = extraData && (
    extraData.po_number ||
    extraData.supplier_name ||
    extraData.days_late !== undefined ||
    extraData.items ||
    extraData.location ||
    extraData.task_title ||
    extraData.stage_name ||
    extraData.site_name ||
    extraData.due_date ||
    extraData.end_date ||
    extraData.days_remaining !== undefined
  );

  // Map notification category/priority to icon and color
  const getIconAndColor = (notif) => {
    const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL)
      ? `${process.env.PUBLIC_URL}/assets/icons/notification`
      : '/assets/icons/notification';
    if (notif.priority === 'urgent') {
      return { icon: `${base}/ic_alert.svg`, color: '#D32F2F', bg: '#FFF3F1' };
    }
    switch (notif.category) {
      case 'APPROVAL':
        return { icon: `${base}/ic_approval.svg`, color: '#1976D2', bg: '#E3F2FD' };
      case 'DEADLINE':
        return { icon: `${base}/ic_deadline.svg`, color: '#FF9800', bg: '#FFF8E1' };
      case 'REMINDER':
        return { icon: `${base}/ic_reminder.svg`, color: '#43A047', bg: '#E8F5E9' };
      case 'ALERT':
        return { icon: `${base}/ic_alert.svg`, color: '#D32F2F', bg: '#FFF3F1' };
      default:
        return { icon: `${base}/ic_notification.svg`, color: '#637381', bg: '#F4F6F8' };
    }
  };

  const { icon, color, bg } = getIconAndColor(notification);

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: bg,
        }}
      >
        <Box
          component="img"
          src={icon}
          alt={notification.category || 'notification'}
          sx={{ width: 28, height: 28, color }}
          onError={e => {
            const fallback = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL)
              ? `${process.env.PUBLIC_URL}/assets/icons/notification/ic_notification.svg`
              : '/assets/icons/notification/ic_notification.svg';
            if (!e.target.src.endsWith('ic_notification.svg')) {
              e.target.onerror = null;
              e.target.src = fallback;
            } else {
              e.target.onerror = null;
            }
          }}
        />
      </Stack>
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Typography
          variant="subtitle2"
          sx={{
            color: notification.is_read ? 'text.secondary' : 'primary.main',
            fontWeight: notification.is_read ? 500 : 700,
            fontSize: 15,
            mb: 0.5,
            letterSpacing: 0.1,
          }}
        >
          {notification.title}
        </Typography>
      }
      secondary={
        <Stack spacing={0.75}>
          {notification.message && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              {notification.message}
            </Typography>
          )}

          {hasExtraDetails && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                endIcon={
                  <Iconify
                    icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                    width={16}
                  />
                }
                sx={{
                  py: 0.25,
                  px: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 1,
                  borderColor: 'divider',
                  textTransform: 'none',
                }}
              >
                {expanded ? 'Hide Details' : 'View Details'}
              </Button>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'background.neutral',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    fontSize: 12,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {extraData.po_number && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        PO Number:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        #{extraData.po_number}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.supplier_name && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Supplier:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.supplier_name}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.location && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Site / Location:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.location}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.task_title && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Task:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.task_title}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.stage_name && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Stage:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.stage_name}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.site_name && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Project:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.site_name} {extraData.site_code ? `(${extraData.site_code})` : ''}
                      </Typography>
                    </Stack>
                  )}

                  {(extraData.due_date || extraData.end_date) && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Deadline:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {extraData.due_date || extraData.end_date}
                      </Typography>
                    </Stack>
                  )}

                  {extraData.days_remaining !== undefined && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Status:
                      </Typography>
                      <Label
                        color={getDaysRemainingColor(extraData.days_remaining)}
                        sx={{ height: 20, fontSize: 11 }}
                      >
                        {getDaysRemainingText(extraData.days_remaining)}
                      </Label>
                    </Stack>
                  )}

                  {extraData.days_late !== undefined && (
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Overdue:
                      </Typography>
                      <Label color={getOverdueColor(extraData.days_late)} sx={{ height: 20, fontSize: 11 }}>
                        {getOverdueText(extraData.days_late)}
                      </Label>
                    </Stack>
                  )}

                  {Array.isArray(extraData.items) && extraData.items.length > 0 && (
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      <Divider sx={{ my: 0.5 }} />
                      {extraData.items.map((item, idx) => (
                        <Stack
                          key={idx}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            p: 0.75,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              #{item.po_number || item.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                              {item.supplier_name || item.site_name || ''}
                            </Typography>
                          </Box>
                          {item.days_late !== undefined && (
                            <Label color="error" sx={{ height: 18, fontSize: 10 }}>
                              {item.days_late}d late
                            </Label>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ typography: 'caption', color: notification.is_read ? 'text.disabled' : 'primary.main', fontWeight: notification.is_read ? 400 : 600, pt: 0.25 }}
          >
            <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
              {notification.category}
            </Typography>
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
            <Typography variant="caption" sx={{ color: '#919EAB' }}>
              {fToNow(notification.created_on || notification.createdAt)}
            </Typography>
          </Stack>
        </Stack>
      }
    />
  );

  const renderUnReadBadge = !notification.is_read && (
    <Box
      sx={{
        top: 18,
        left: 36,
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        position: 'absolute',
        boxShadow: '0 0 0 2px #fff',
      }}
    />
  );

  const friendAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  const projectAction = (
    <Stack alignItems="flex-start">
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: 'text.secondary',
          bgcolor: 'background.neutral',
        }}
      >
        {reader(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained">
        Reply
      </Button>
    </Stack>
  );

  const fileAction = (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        pl: 1,
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.neutral',
      }}
    >
      <FileThumbnail
        file="http://localhost:8080/httpsdesign-suriname-2015.mp3"
        sx={{ width: 40, height: 40 }}
      />

      <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} flexGrow={1} sx={{ minWidth: 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" component="div" sx={{ color: 'text.secondary' }} noWrap>
              design-suriname-2015.mp3
            </Typography>
          }
          secondary={
            <Stack
              direction="row"
              alignItems="center"
              sx={{ typography: 'caption', color: 'text.disabled' }}
              divider={
                <Box
                  sx={{
                    mx: 0.5,
                    width: 2,
                    height: 2,
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                  }}
                />
              }
            >
              <span>2.3 GB</span>
              <span>30 min ago</span>
            </Stack>
          }
        />

        <Button size="small" variant="outlined">
          Download
        </Button>
      </Stack>
    </Stack>
  );

  const tagsAction = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Stack>
  );

  const paymentAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Pay
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      onClick={onClick}
      sx={{
        p: 2.2,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        backgroundColor: !notification.is_read ? 'rgba(0,167,111,0.08)' : 'background.paper',
        transition: 'background 0.2s',
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: !notification.is_read ? 'rgba(0,167,111,0.16)' : 'action.hover',
        },
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {/* Optionally, add more details or actions here */}
      </Stack>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object,
  onClick: PropTypes.func,
};

// ----------------------------------------------------------------------

function reader(data) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
